import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Images
import CatchingOnEllie from '@/media/images/Ellie_AboutPage.jpg';
import CatchingOnLola from '@/media/images/Lola_AboutPage.jpg';
import CatchingOnLogo from '@/media/images/CatchingOn_Logo.png';

const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
};

export const About = () => {
    return (
        <div data-testid="about-page" className="bg-brand-periwinkle min-h-screen pt-40 pb-20 px-6 md:px-12">
            <div className="max-w-7xl mx-auto flex flex-col gap-12">

                {/* Top row: Meet Ellie text | Ellie photo */}
                <div className="flex flex-col lg:flex-row gap-12">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.7 }}
                        className="lg:w-1/2"
                    >
                        <h1 className="text-5xl lg:text-6xl font-black text-black uppercase leading-none mb-6">
                            MEET<br />
                            <span className="bg-brand-lime px-2">ELLIE</span>
                        </h1>
                        <p className="text-lg text-black leading-relaxed mb-3">Hello!</p>
                        <p className="text-lg text-black leading-relaxed mb-3">Lovely to meet you!</p>
                        <p className="text-lg text-black leading-relaxed mb-6">
                           I’m the co-founder of Catching On and an experienced marketer who has worked across a wide range of industries from non-profits and start-ups to energy and fitness. 
                           It’s fair to say there isn’t much I haven’t dabbled in. After years of helping organisations with their marketing, my friends often joked that I run my life like I run a business… 
                           so eventually I decided to start one.
                        </p>
                        <p className="text-lg text-black leading-relaxed">
                            Fancy a chat? Book a 15-minute call and let's see how we can work together.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="lg:w-1/2 flex flex-col items-center"
                    >
                        <div className="relative w-full max-w-sm mx-auto">
                            <div className="full-arch aspect-[3/4]">
                                <img
                                    src={CatchingOnEllie}
                                    alt="Ellie, Founder of Catching On"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute bottom-4 left-4 bg-brand-lime border-2 border-black px-4 py-2">
                                <p className="font-black text-sm text-black uppercase tracking-wide leading-tight">ELLIE SIVITER</p>
                                <p className="font-medium text-xs text-black uppercase tracking-wider">FOUNDER</p>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom row: Lola arch | logo + story + button */}
                <div className="flex flex-col lg:flex-row gap-12 items-stretch">

                    {/* Left: Lola arch */}
                    <motion.div
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={fadeIn}
                        className="lg:w-1/2"
                    >
                        <div className="relative w-full max-w-sm h-full">
                            <div className="full-arch aspect-[3/4]">
                                <img
                                    src={CatchingOnLola}
                                    alt="Lola the office dog"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <svg
                                className="absolute bottom-16 left-16 w-20 h-20"
                                viewBox="0 0 60 70"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <defs>
                                    <marker id="arrow" markerWidth="12" markerHeight="12" refX="6" refY="6" orient="auto">
                                        <path d="M0 0 L8 6 L0 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </marker>
                                </defs>
                                <path
                                    d="M10 65 Q5 30 45 8"
                                    stroke="black"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    fill="none"
                                    markerEnd="url(#arrow)"
                                />
                            </svg>
                            <div className="absolute bottom-4 left-4 bg-brand-lime border-2 border-black px-4 py-2">
                                <p className="font-black text-sm text-black uppercase tracking-wide leading-tight">MEET LOLA</p>
                                <p className="font-medium text-xs text-black uppercase tracking-wider">BARKETING ASSISTANT</p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right: logo + story + button, filling the same height as the arch */}
                    <motion.div
                        initial="initial"
                        whileInView="animate"
                        viewport={{ once: true, margin: "-80px" }}
                        variants={fadeIn}
                        className="lg:w-1/2 flex flex-col"
                    >
                        <img src={CatchingOnLogo} alt="Catching On" className="h-64 w-auto object-contain mt-4 mb-2" />
                        <p className="text-lg text-black mb-6 leading-relaxed">
                            Catching On launched in 2024 to make marketing simpler for businesses.
                            Instead of hunting down a copywriter, a designer and an email marketer,
                            you can find everything you need in one place.
                            Based in Worcester, we've built a fantastic network and work with
                            businesses both locally and across the UK.
                        </p>
                        <div>
                            <Link to="/contact">
                                <button className="btn-lime" data-testid="get-in-touch-btn">
                                    BOOK A MEETING
                                </button>
                            </Link>
                        </div>
                    </motion.div>

                </div>

            </div>
        </div>
    );
};

export default About;
